import { ApiError } from '@ghp/api-client'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { sessionQuery, SIGNED_OUT, type AuthApi, type BackOfficeSession } from './session'

/** Aux deux tiers des 15 minutes du jeton (M23) : jamais expiré tant que l'onglet vit. */
export const RENEWAL_INTERVAL_MS = 10 * 60_000

/**
 * Renouvelle le jeton d'accès pendant la session, monté une fois dans la coquille.
 *
 * Palliatif d'ici le câblage complet D21 (relance sur 401 au niveau des appels, #92) : sans
 * lui, tout onglet ouvert plus de 15 minutes voit ses appels authentifiés refusés jusqu'au
 * rechargement. Répéter `/auth/refresh` est sans effet de bord — pas de rotation du jeton de
 * rafraîchissement (M23, réserve `🔵`).
 */
export function useSessionRenewal(api: AuthApi, onSignedOut?: () => void): void {
  const queryClient = useQueryClient()
  // Une ref pour ne pas réarmer le minuteur à chaque rendu : `navigate` change d'identité.
  const signedOut = useRef(onSignedOut)
  signedOut.current = onSignedOut

  useEffect(() => {
    const timer = setInterval(() => {
      void renewSession(api).then((renewed) => {
        if (!renewed) return
        queryClient.setQueryData(sessionQuery(api).queryKey, renewed)
        if (renewed.state === 'signed_out') signedOut.current?.()
      })
    }, RENEWAL_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [api, queryClient])
}

/**
 * ⚠️ Seul un refus explicite (401) déconnecte. Tout autre échec — coupure, serveur injoignable,
 * 5xx — laisse la session en place et s'oublie : c'est l'objection notée dans `session.ts`
 * contre un rafraîchissement périodique naïf, qui déconnecterait sur une coupure d'une
 * seconde en pleine saisie. Le passage suivant réessaie.
 */
async function renewSession(api: AuthApi): Promise<BackOfficeSession | undefined> {
  try {
    const { accessToken } = await api.refresh()
    return { state: 'signed_in', accessToken }
  } catch (failure) {
    return failure instanceof ApiError && failure.status === 401 ? SIGNED_OUT : undefined
  }
}
